import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { Trash2 } from 'lucide-react';
import { db } from '@/firebase';
import './OrderTaskPrint.css';
import { DEFAULT_ORDER_PRINT_SETTINGS, getOrderPrintSettings, type OrderPrintSettings } from '@/utils/orderPrintSettings';

interface TaskScore {
    label: string;
    score: number | null;
    max: number;
}

interface TaskReview {
    chapter: number;
    qno: number;
    answer: string;
    explanation: string;
    explanationHeight?: number;
    pageBreakBefore?: boolean;
}

interface QuizItem {
    id: string;
    Chapter: number;
    Qno: number;
    CollectAnswer?: string;
    Explanation?: string;
}

type SaveStatus = 'draft' | 'saved';
type ActiveTab = 'task' | 'letter';
type PrintMode = 'all' | 'main' | 'letter';

const SCORE_ROW_COUNT = 3;
const TASK_CHAPTER_OPTIONS = [1, 2, 3];
const TASK_QNO_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    const savedHeight = Number(textarea.dataset.savedHeight || 0);
    const nextHeight = Math.max(textarea.scrollHeight, textarea.offsetHeight, savedHeight);
    textarea.style.height = `${nextHeight}px`;
};

const createDefaultScores = (count: number): TaskScore[] =>
    Array.from({ length: count }, (_, index) => ({
        label: `問題${index + 1}`,
        score: null,
        max: 10,
    }));

const normalizeScores = (scores: TaskScore[]) => {
    const base = Array.from({ length: SCORE_ROW_COUNT }, (_, index) => ({
        label: `問題${index + 1}`,
        score: null,
        max: 10,
    }));
    return base.map((fallback, index) => {
        const existing = scores[index];
        return existing
            ? {
                  ...fallback,
                  ...existing,
                  label: existing.label || fallback.label,
              }
            : fallback;
    });
};

const sumScores = (scores: TaskScore[]) =>
    scores.reduce(
        (acc, item) => ({
            score: acc.score + (Number.isFinite(item.score) ? item.score : 0),
            max: acc.max + (Number.isFinite(item.max) ? item.max : 0),
        }),
        { score: 0, max: 0 }
    );

const sanitizeTaskReviews = (reviews: TaskReview[]): TaskReview[] =>
    reviews.map((review) => {
        const sanitized = { ...review } as Record<string, unknown>;
        Object.keys(sanitized).forEach((key) => {
            if (sanitized[key] === undefined) {
                delete sanitized[key];
            }
        });
        return sanitized as TaskReview;
    });

const formatCorrectMark = (value: unknown) => (String(value) === '1' ? '○' : '×');

const createDefaultReview = (quizItem?: QuizItem): TaskReview => ({
    chapter: quizItem?.Chapter ?? 1,
    qno: quizItem?.Qno ?? 1,
    answer: quizItem?.CollectAnswer ? formatCorrectMark(quizItem.CollectAnswer) : '',
    explanation: quizItem?.Explanation || '',
});

export default function OrderTaskPrint() {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [orderData, setOrderData] = useState<any>(null);
    const [quizItems, setQuizItems] = useState<QuizItem[]>([]);
    const [draftTaskScores, setDraftTaskScores] = useState<TaskScore[]>([]);
    const [draftTaskReviews, setDraftTaskReviews] = useState<TaskReview[]>([]);
    const [savedTaskScores, setSavedTaskScores] = useState<TaskScore[]>([]);
    const [savedTaskReviews, setSavedTaskReviews] = useState<TaskReview[]>([]);
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('draft');
    const [shikakuData, setShikakuData] = useState<any>(null);
    const [userName, setUserName] = useState<string>('');
    const [activeTab, setActiveTab] = useState<ActiveTab>('task');
    const [printMode, setPrintMode] = useState<PrintMode>('all');
    const [isPrintMenuOpen, setIsPrintMenuOpen] = useState(false);
    const [orderPrintSettings, setOrderPrintSettings] = useState<OrderPrintSettings>(DEFAULT_ORDER_PRINT_SETTINGS);
    const reviewContainerRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        document.body.classList.add('order-task-print');
        return () => {
            document.body.classList.remove('order-task-print');
        };
    }, []);

    useEffect(() => {
        const handleAfterPrint = () => {
            setPrintMode('all');
        };
        window.addEventListener('afterprint', handleAfterPrint);
        return () => {
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, []);

    useEffect(() => {
        let active = true;

        const loadOrderPrintSettings = async () => {
            try {
                const data = await getOrderPrintSettings();
                if (active) {
                    setOrderPrintSettings(data);
                }
            } catch (error) {
                console.error('Error loading order print settings:', error);
            }
        };

        loadOrderPrintSettings();

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const root = document.documentElement;
        const previousValues = {
            marginTopBottom: root.style.getPropertyValue('--order-print-page-margin-top-bottom'),
            paddingTop: root.style.getPropertyValue('--order-print-page-padding-top'),
            paddingBottom: root.style.getPropertyValue('--order-print-page-padding-bottom'),
            reviewFontSize: root.style.getPropertyValue('--order-print-review-font-size'),
            reviewCellPadding: root.style.getPropertyValue('--order-print-review-cell-padding'),
            reviewTextareaPadding: root.style.getPropertyValue('--order-print-review-textarea-padding'),
            taskLetterTitleFontSize: root.style.getPropertyValue('--order-print-task-letter-title-font-size'),
            examLetterTitleFontSize: root.style.getPropertyValue('--order-print-exam-letter-title-font-size'),
        };

        root.style.setProperty('--order-print-page-margin-top-bottom', `${orderPrintSettings.pageMarginTopBottomMm}mm`);
        root.style.setProperty('--order-print-page-padding-top', `${orderPrintSettings.pagePaddingTopMm}mm`);
        root.style.setProperty('--order-print-page-padding-bottom', `${orderPrintSettings.pagePaddingBottomMm}mm`);
        root.style.setProperty('--order-print-review-font-size', `${orderPrintSettings.reviewFontSizePt}pt`);
        root.style.setProperty('--order-print-review-cell-padding', `${orderPrintSettings.reviewCellPaddingPx}px`);
        root.style.setProperty('--order-print-review-textarea-padding', `${orderPrintSettings.reviewTextareaPaddingPx}px`);
        root.style.setProperty('--order-print-task-letter-title-font-size', `${orderPrintSettings.taskLetterTitleFontSizePt}pt`);
        root.style.setProperty('--order-print-exam-letter-title-font-size', `${orderPrintSettings.examLetterTitleFontSizePt}pt`);

        const pageMarginTopBottom = orderPrintSettings.pageMarginTopBottomMm;
        const pagePaddingTop = orderPrintSettings.pagePaddingTopMm;
        const pagePaddingBottom = orderPrintSettings.pagePaddingBottomMm;

        const existingStyleEl = document.getElementById('order-task-print-page-rules');
        existingStyleEl?.remove();

        const styleEl = document.createElement('style');
        styleEl.id = 'order-task-print-page-rules';
        styleEl.textContent = `
            @page {
                size: A4;
                margin: ${pageMarginTopBottom}mm 0;
            }

            @page order-main {
                size: A4;
                margin: ${pageMarginTopBottom + pagePaddingTop}mm 0 ${pageMarginTopBottom + pagePaddingBottom}mm;
            }

            @page order-letter {
                size: A4;
                margin: ${pageMarginTopBottom + pagePaddingTop}mm 0 ${pageMarginTopBottom + pagePaddingBottom}mm;
            }
        `;
        document.head.appendChild(styleEl);

        return () => {
            root.style.setProperty('--order-print-page-margin-top-bottom', previousValues.marginTopBottom);
            root.style.setProperty('--order-print-page-padding-top', previousValues.paddingTop);
            root.style.setProperty('--order-print-page-padding-bottom', previousValues.paddingBottom);
            root.style.setProperty('--order-print-review-font-size', previousValues.reviewFontSize);
            root.style.setProperty('--order-print-review-cell-padding', previousValues.reviewCellPadding);
            root.style.setProperty('--order-print-review-textarea-padding', previousValues.reviewTextareaPadding);
            root.style.setProperty('--order-print-task-letter-title-font-size', previousValues.taskLetterTitleFontSize);
            root.style.setProperty('--order-print-exam-letter-title-font-size', previousValues.examLetterTitleFontSize);
            styleEl.remove();
        };
    }, [orderPrintSettings]);

    useEffect(() => {
        const container = reviewContainerRef.current;
        if (!container) {
            return;
        }
        const textareas = container.querySelectorAll<HTMLTextAreaElement>('textarea[data-auto-resize]');
        textareas.forEach(autoResizeTextarea);
    }, [draftTaskReviews, activeTab]);

    useEffect(() => {
        const handleBeforePrint = () => {
            const container = reviewContainerRef.current;
            if (!container) {
                return;
            }
            const textareas = container.querySelectorAll<HTMLTextAreaElement>('textarea[data-auto-resize]');
            textareas.forEach(autoResizeTextarea);
        };
        window.addEventListener('beforeprint', handleBeforePrint);
        return () => {
            window.removeEventListener('beforeprint', handleBeforePrint);
        };
    }, []);

    useEffect(() => {
        const fetchOrder = async () => {
            if (!id) {
                setError('Order IDが指定されていません');
                setLoading(false);
                return;
            }

            try {
                const orderDoc = await getDoc(doc(db, 'Order', id));
                if (!orderDoc.exists()) {
                    setError('Orderが見つかりませんでした');
                    setLoading(false);
                    return;
                }

                const data = { orderId: orderDoc.id, ...orderDoc.data() };
                setOrderData(data);

                let shikakuRef = data.shikakuId;
                if (shikakuRef && !shikakuRef.path) {
                    shikakuRef = doc(db, 'Shikaku', shikakuRef);
                }

                if (shikakuRef) {
                    const shikakuDoc = await getDoc(shikakuRef);
                    if (shikakuDoc.exists()) {
                        setShikakuData(shikakuDoc.data());
                    }
                }

                const userRef = data.UserRef || data.userId;
                if (userRef) {
                    const userDocRef = userRef.path ? userRef : doc(db, 'User', userRef);
                    const userSnapshot = await getDoc(userDocRef);
                    if (userSnapshot.exists()) {
                        const userData = userSnapshot.data();
                        setUserName(userData.display_name || userData.display_Name || '');
                    }
                }

                const storedScores = Array.isArray(data.taskScores) ? data.taskScores : null;
                const storedReviews = Array.isArray(data.taskReviews) ? data.taskReviews : null;

                let quizData: QuizItem[] = [];
                if (shikakuRef) {
                    const quizQuery = query(collection(db, 'Quiz'), where('SKref', '==', shikakuRef));
                    const quizSnapshot = await getDocs(quizQuery);
                    quizData = quizSnapshot.docs
                        .map((quizDoc) => ({ id: quizDoc.id, ...(quizDoc.data() as QuizItem) }))
                        .sort((a, b) => {
                            if (a.Chapter !== b.Chapter) return a.Chapter - b.Chapter;
                            return a.Qno - b.Qno;
                        });
                    setQuizItems(quizData);
                }

                if (storedScores || storedReviews) {
                    const nextScores = normalizeScores(storedScores ?? []);
                    const nextReviews = Array.isArray(storedReviews) ? storedReviews : [createDefaultReview(quizData[0])];
                    setDraftTaskScores(nextScores);
                    setDraftTaskReviews(nextReviews);
                    setSavedTaskScores(nextScores);
                    setSavedTaskReviews(nextReviews);
                    setSaveStatus('saved');
                } else if (shikakuRef) {
                    const initialReviews = [createDefaultReview(quizData[0])];
                    const initialScores = normalizeScores(createDefaultScores(SCORE_ROW_COUNT));
                    setDraftTaskReviews(initialReviews);
                    setDraftTaskScores(initialScores);
                    setSavedTaskReviews([]);
                    setSavedTaskScores([]);
                    setSaveStatus('draft');
                } else {
                    setDraftTaskScores(normalizeScores(createDefaultScores(SCORE_ROW_COUNT)));
                }
            } catch (err: any) {
                console.error('Error fetching order task data:', err);
                setError(err?.message || 'Orderの取得に失敗しました');
            } finally {
                setLoading(false);
            }
        };

        fetchOrder();
    }, [id]);

    const savedTotals = useMemo(() => sumScores(savedTaskScores), [savedTaskScores]);

    const scoreComment = useMemo(() => {
        const score = savedTotals.score;
        const totalScoreLabel = String(score);
        const replaceScore = (text: string) => text.replace(/\[totalscore\]/g, totalScoreLabel);

        if (score >= 30) {
            return replaceScore(`この度は、添削課題をご返送いただきまして誠にありがとうございます！
本試験に向けての勉強は進んでおりますでしょうか。
今回返送いただきました添削課題を採点させて頂きましたところ30点/30点となり、満点でした！学習状況としては完璧で、この調子であれば本試験についても問題ないかと存じます。
引き続き学習を進めて頂ければと思います。なかなか日常で触れる機会のない言葉も多く出てきますので、最後まで気を抜かず、本試験での合格をお待ち申し上げます！`);
        }

        if (score >= 23) {
            return replaceScore(`この度は、添削課題をご返送いただきまして誠にありがとうございます！
本試験に向けての勉強は進んでおりますでしょうか。
今回返送いただきました添削課題を採点させて頂きましたところ[totalscore]点/30点となり、かなりの正答率で素晴らしい学習状況でした。この調子であれば本試験も合格ラインに到達することが期待できます。
本試験では7割の正答率が必要となりますので、今回の進捗状況であれば問題ないかと存じますが、日常では見慣れない語句も多数出てきますので言葉自体を忘れやすくなっております。
なるべく頻度高くテキストに触れて頂くことで習熟が早まることが期待できますので短い時間でも多くの機会を作って頂けると幸いです。
本試験に向けて、引き続き学習をよろしくお願い申し上げます。`);
        }

        if (score >= 15) {
            return replaceScore(`この度は、添削課題をご返送いただきまして誠にありがとうございます！
本試験に向けての勉強は進んでおりますでしょうか。
今回返送いただきました添削課題を採点させて頂きましたところ[totalscore]点/30点となり、半分以上正答出来ておりました。
本試験では7割の正答率が必要となりますので、今回不正答となった箇所を中心に改めてテキストでの学習を進めて頂ければと思います。また、日常では見慣れない語句も多数出てきますので言葉自体を忘れやすくなっております。
なるべく頻度高くテキストに触れて頂くことで習熟が早まることが期待できますので短い時間でも多くの機会を作って頂けると幸いです。
本試験に向けて、引き続き学習をよろしくお願い申し上げます。`);
        }

        return replaceScore(`この度は、添削課題をご返送いただきまして誠にありがとうございます！
本試験に向けての勉強は進んでおりますでしょうか。
今回返送いただきました添削課題を採点させて頂きましたところ[totalscore]点/30点となり、今一度テキストを見返して頂く必要があると判断させて頂きました。
本試験では7割の正答率が必要になりますので、今回の課題での不正答箇所を中心に学習を進めて頂き、本試験に臨めると幸いでございます。`);
    }, [savedTotals.score]);

    const handleScoreChange = (index: number, field: keyof TaskScore, value: string) => {
        setDraftTaskScores((prev) =>
            prev.map((item, idx) => {
                if (idx !== index) return item;
                if (field === 'label') {
                    return { ...item, label: value };
                }
                if (value === '') {
                    return { ...item, [field]: null };
                }
                const numeric = Number(value);
                return { ...item, [field]: Number.isNaN(numeric) ? null : numeric };
            })
        );
        setSaveStatus('draft');
    };

    const handleReviewChange = (index: number, field: keyof TaskReview, value: string | number) => {
        setDraftTaskReviews((prev) =>
            prev.map((item, idx) => {
                if (idx !== index) return item;
                if (field === 'chapter' || field === 'qno') {
                    const numeric = typeof value === 'number' ? value : Number(value);
                    const nextItem = { ...item, [field]: Number.isNaN(numeric) ? 0 : numeric };
                    const matchedQuiz = quizItems.find(
                        (quiz) => quiz.Chapter === nextItem.chapter && quiz.Qno === nextItem.qno
                    );
                    if (matchedQuiz) {
                        return {
                            ...nextItem,
                            answer: formatCorrectMark(matchedQuiz.CollectAnswer),
                            explanation: matchedQuiz.Explanation || '',
                            ...(typeof item.explanationHeight === 'number'
                                ? { explanationHeight: item.explanationHeight }
                                : {}),
                        };
                    }
                    return nextItem;
                }
                return { ...item, [field]: value };
            })
        );
        setSaveStatus('draft');
    };

    const handleReviewHeightChange = (index: number, height: number) => {
        setDraftTaskReviews((prev) =>
            prev.map((item, idx) => (idx === index ? { ...item, explanationHeight: height } : item))
        );
        setSaveStatus('draft');
    };

    const handleRemoveReviewRow = () => {
        setDraftTaskReviews((prev) => prev.slice(0, -1));
        setSaveStatus('draft');
    };

    const handleRemoveReviewRowAt = (index: number) => {
        setDraftTaskReviews((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
        setSaveStatus('draft');
    };

    const handleMoveReviewRow = (index: number, direction: 'up' | 'down') => {
        setDraftTaskReviews((prev) => {
            const nextIndex = direction === 'up' ? index - 1 : index + 1;
            if (nextIndex < 0 || nextIndex >= prev.length) return prev;
            const next = [...prev];
            const [moved] = next.splice(index, 1);
            next.splice(nextIndex, 0, moved);
            return next;
        });
        setSaveStatus('draft');
    };

    const handleToggleReviewPageBreak = (index: number) => {
        setDraftTaskReviews((prev) =>
            prev.map((item, rowIndex) =>
                rowIndex === index ? { ...item, pageBreakBefore: !item.pageBreakBefore } : item
            )
        );
        setSaveStatus('draft');
    };

    const handleAddReviewRow = () => {
        setDraftTaskReviews((prev) => [
            ...prev,
            {
                ...createDefaultReview(),
                chapter: TASK_CHAPTER_OPTIONS[0],
                qno: TASK_QNO_OPTIONS[0],
            },
        ]);
        setSaveStatus('draft');
    };

    const handlePrint = (mode: PrintMode) => {
        setPrintMode(mode);
        setIsPrintMenuOpen(false);
        if (mode === 'letter') {
            setActiveTab('letter');
        }
        if (mode === 'main') {
            setActiveTab('task');
        }
        setTimeout(() => window.print(), 100);
    };

    const handleSave = async () => {
        if (!id || saving) return;
        setSaving(true);
        try {
            const sanitizedTaskReviews = sanitizeTaskReviews(draftTaskReviews);
            await updateDoc(doc(db, 'Order', id), {
                taskScores: draftTaskScores,
                taskReviews: sanitizedTaskReviews,
                taskScoredAt: Timestamp.now(),
            });
            setSavedTaskScores(draftTaskScores);
            setSavedTaskReviews(sanitizedTaskReviews);
            setDraftTaskReviews(sanitizedTaskReviews);
            setSaveStatus('saved');
            window.location.reload();
        } catch (err: any) {
            console.error('Error updating order task data:', err);
            alert(err?.message || '添削課題の更新に失敗しました');
        } finally {
            setSaving(false);
        }
    };

    const draftTotals = useMemo(() => sumScores(draftTaskScores), [draftTaskScores]);
    const scoredAtLabel = useMemo(() => {
        if (!orderData?.taskScoredAt?.toDate) return '-';
        return orderData.taskScoredAt.toDate().toLocaleDateString('ja-JP');
    }, [orderData?.taskScoredAt]);
    const letterDateLabel = scoredAtLabel === '-' ? new Date().toLocaleDateString('ja-JP') : scoredAtLabel;
    const letterRecipient = userName || '受講者';
    const shikakuName = shikakuData?.ShikakuName || '資格';
    if (loading) {
        return <div className="min-h-screen flex items-center justify-center">読み込み中...</div>;
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center text-red-600">
                {error}
            </div>
        );
    }

    const reviewMark = (value: string) => {
        if (value === '○' || value === '×') return value;
        return formatCorrectMark(value);
    };

    return (
        <div
            className={`order-task-print print-mode-${printMode}`}
            style={{
                ['--order-print-page-margin-top-bottom' as const]: `${orderPrintSettings.pageMarginTopBottomMm}mm`,
                ['--order-print-page-padding-top' as const]: `${orderPrintSettings.pagePaddingTopMm}mm`,
                ['--order-print-page-padding-bottom' as const]: `${orderPrintSettings.pagePaddingBottomMm}mm`,
                ['--order-print-review-font-size' as const]: `${orderPrintSettings.reviewFontSizePt}pt`,
                ['--order-print-review-cell-padding' as const]: `${orderPrintSettings.reviewCellPaddingPx}px`,
                ['--order-print-review-textarea-padding' as const]: `${orderPrintSettings.reviewTextareaPaddingPx}px`,
                ['--order-print-task-letter-title-font-size' as const]: `${orderPrintSettings.taskLetterTitleFontSizePt}pt`,
                ['--order-print-exam-letter-title-font-size' as const]: `${orderPrintSettings.examLetterTitleFontSizePt}pt`,
            }}>
            <div className="print-toolbar">
                <div className="print-breadcrumbs">
                    <Link to="/admin/orders">一覧</Link>
                    <span>&gt;</span>
                    <Link to={`/admin/orders/${id}`}>注文詳細</Link>
                    <span>&gt;</span>
                    <span className="current">添削課題</span>
                </div>
                <div className="print-actions">
                    <div className="print-menu">
                        <button
                            type="button"
                            onClick={() => setIsPrintMenuOpen((prev) => !prev)}
                            className="print-button"
                        >
                            印刷
                        </button>
                        {isPrintMenuOpen && (
                            <div className="print-menu-list">
                                <button type="button" onClick={() => handlePrint('all')}>
                                    両方印刷
                                </button>
                                <button type="button" onClick={() => handlePrint('main')}>
                                    添削課題のみ印刷
                                </button>
                                <button type="button" onClick={() => handlePrint('letter')}>
                                    送付状のみ印刷
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="print-tabs">
                <button
                    type="button"
                    className={activeTab === 'task' ? 'is-active' : ''}
                    onClick={() => setActiveTab('task')}
                >
                    添削課題
                </button>
                <button
                    type="button"
                    className={activeTab === 'letter' ? 'is-active' : ''}
                    onClick={() => setActiveTab('letter')}
                >
                    送付状
                </button>
            </div>

            <div className={`tab-panel ${activeTab === 'task' ? 'is-active' : ''}`} data-print-target="main">
                <div className="a4-page">
                    <header>
                        <h1>添削課題採点結果</h1>
                    </header>

                    <section className="info-table">
                        <table>
                            <tbody>
                                <tr>
                                    <th>氏名</th>
                                    <td>{userName || '-'}</td>
                                    <th>採点日</th>
                                    <td>{scoredAtLabel}</td>
                                </tr>
                                <tr>
                                    <th>受験資格名</th>
                                    <td colSpan={3}>{shikakuData?.ShikakuName || '-'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>

                    <section>
                        <div className="section-header">
                            <h2>採点結果内訳</h2>
                        </div>
                        <div className="total-score">
                            合計得点: <span>{draftTotals.score}点</span> / {draftTotals.max}点満点
                        </div>
                        <table className="score-table">
                            <thead>
                                <tr>
                                    <th className="center">項目</th>
                                    <th className="center">得点</th>
                                    <th className="center">配点</th>
                                </tr>
                            </thead>
                            <tbody>
                                {draftTaskScores.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="center">
                                            採点項目がありません
                                        </td>
                                    </tr>
                                )}
                                {draftTaskScores.map((scoreItem, index) => (
                                    <tr key={`${scoreItem.label}-${index}`}>
                                        <td className="center">
                                            <input
                                                type="text"
                                                value={scoreItem.label}
                                                onChange={(e) => handleScoreChange(index, 'label', e.target.value)}
                                            />
                                        </td>
                                        <td className="center">
                                            <div className="score-input">
                                                <input
                                                    type="number"
                                                    max={10}
                                                    value={scoreItem.score ?? ''}
                                                    onChange={(e) => handleScoreChange(index, 'score', e.target.value)}
                                                />
                                                <span>点</span>
                                            </div>
                                        </td>
                                        <td className="center">
                                            <div className="score-input">
                                                <input
                                                    type="number"
                                                    max={10}
                                                    value={scoreItem.max}
                                                    onChange={(e) => handleScoreChange(index, 'max', e.target.value)}
                                                />
                                                <span>点</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    <section ref={reviewContainerRef}>
                        <div className="section-header">
                            <h2>項目解説</h2>
                            <div className="section-actions">
                                <button onClick={handleSave} disabled={saving} className="print-save-button">
                                    {saving ? '保存中...' : '更新'}
                                </button>
                                <button type="button" onClick={handleAddReviewRow} className="link-button">
                                    行追加
                                </button>
                                <button type="button" onClick={handleRemoveReviewRow} className="link-button">
                                    行削除
                                </button>
                            </div>
                        </div>
                        <table className="review-table">
                            <colgroup>
                                <col className="review-col-number" />
                                <col className="review-col-mark" />
                                <col className="review-col-detail" />
                                <col className="review-col-actions" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className="center">番号</th>
                                    <th className="center">正解</th>
                                    <th>正解と解説</th>
                                    <th className="center review-actions" aria-label="操作" />
                                </tr>
                            </thead>
                            <tbody>
                                {draftTaskReviews.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="center">
                                            レビュー項目がありません
                                        </td>
                                    </tr>
                                )}
                                {draftTaskReviews.map((review, index) => (
                                    <tr key={`${review.chapter}-${review.qno}-${index}`} className={review.pageBreakBefore ? 'review-page-break' : undefined}>
                                        <td className="center">
                                            <div className="review-number">
                                                <span className="review-number-text">
                                                    {review.chapter}-{review.qno}
                                                </span>
                                                <select
                                                    value={review.chapter}
                                                    onChange={(e) =>
                                                        handleReviewChange(index, 'chapter', Number(e.target.value))
                                                    }
                                                >
                                                    {TASK_CHAPTER_OPTIONS.map((chapter) => (
                                                        <option key={chapter} value={chapter}>
                                                            {chapter}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span className="review-separator">-</span>
                                                <select
                                                    value={review.qno}
                                                    onChange={(e) => handleReviewChange(index, 'qno', Number(e.target.value))}
                                                >
                                                    {TASK_QNO_OPTIONS.map((qno) => (
                                                        <option key={qno} value={qno}>
                                                            {qno}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </td>
                                        <td className="center">
                                            <span className={reviewMark(review.answer) === '○' ? 'mark-ok' : 'mark-ng'}>
                                                {reviewMark(review.answer)}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="review-detail">
                                                <textarea
                                                    data-auto-resize
                                                    data-saved-height={review.explanationHeight || undefined}
                                                    rows={3}
                                                    value={review.explanation}
                                                    onChange={(e) =>
                                                        handleReviewChange(index, 'explanation', e.target.value)
                                                    }
                                                    onInput={(e) => autoResizeTextarea(e.currentTarget)}
                                                    onMouseUp={(e) =>
                                                        handleReviewHeightChange(index, e.currentTarget.offsetHeight)
                                                    }
                                                    onTouchEnd={(e) =>
                                                        handleReviewHeightChange(index, e.currentTarget.offsetHeight)
                                                    }
                                                    onBlur={(e) =>
                                                        handleReviewHeightChange(index, e.currentTarget.offsetHeight)
                                                    }
                                                    style={{
                                                        height: review.explanationHeight
                                                            ? `${review.explanationHeight}px`
                                                            : undefined,
                                                    }}
                                                />
                                            </div>
                                        </td>
                                        <td className="review-actions center">
                                            <div className="review-action-buttons">
                                                <button
                                                    type="button"
                                                    onClick={() => handleToggleReviewPageBreak(index)}
                                                    className="review-page-break-button"
                                                >
                                                    {review.pageBreakBefore ? '改ページ解除' : '改ページ'}
                                                </button>
                                                <div className="review-inline-actions">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveReviewRow(index, 'up')}
                                                        disabled={index === 0}
                                                        aria-label="上へ移動"
                                                    >
                                                        ↑
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveReviewRow(index, 'down')}
                                                        disabled={index === draftTaskReviews.length - 1}
                                                        aria-label="下へ移動"
                                                    >
                                                        ↓
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveReviewRowAt(index)}
                                                        className="review-delete-button"
                                                        aria-label="行を削除"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    <section>
                        <h2>評価者によるコメント</h2>
                        <div className="comment-box">
                            <p>{scoreComment || 'コメントがありません'}</p>
                        </div>
                    </section>
                </div>
            </div>

            <div className={`tab-panel ${activeTab === 'letter' ? 'is-active' : ''}`} data-print-target="letter">
                <div className="a4-page letter-page">
                    <div className="header-meta">
                        <div className="recipient">{letterRecipient} 様</div>
                        <div className="sender-info">
                            {letterDateLabel}
                            <br />
                            ラーキャリ運営事務局
                        </div>
                    </div>

                    <header>
                        <h1>
                            {shikakuName}資格
                            <br />
                            添削課題の結果について
                        </h1>
                    </header>

                    <main>
                        <p>時下ますますご清栄のこととお喜び申し上げます。</p>
                        <p>
                            この度は{shikakuName}資格の添削課題を提出いただき、誠にありがとうございました。
                        </p>
                        <p>
                            添削結果につきましては、同封の添削課題採点結果に記させていただきました。この結果をもとに、本試験の合格に向けてさらなる躍進を期待しております。
                        </p>

                        <p>
                            今後とも、本事務局へのご理解とご協力を賜りますようよろしくお願い申し上げると共に、皆様の益々のご健勝とご活躍を祈念致します。
                        </p>

                        <section className="inquiry-section">
                            <h2>■お問い合わせ</h2>
                            <div className="inquiry-container">
                                <div className="inquiry-box">
                                    <p>京都府相楽郡精華町光台1丁目7 けいはんなプラザ11F</p>
                                    <p>株式会社RIGWORKS ラーキャリ運営事務局</p>
                                    <p>URL: https://lear-caree.jp</p>
                                    <p>メール: info@lear-caree.jp</p>
                                </div>

                                <div className="qr-box">
                                    <p>公式LINE</p>
                                    <img
                                        className="qr-image"
                                        src="https://firebasestorage.googleapis.com/v0/b/learcareeonline.firebasestorage.app/o/admin%2FM_gainfriends_2dbarcodes_BW.png?alt=media&token=0f624c2f-3a95-43d2-a0fa-86f985ebd998"
                                        alt="公式LINE QRコード"
                                    />
                                </div>
                            </div>
                        </section>
                    </main>

                    <div className="end-mark">以上</div>
                </div>
            </div>
        </div>
    );
}
